import { useLayoutEffect, useRef, useState } from "react";
import { Icon, type IconName } from "../components/Icon";
import { Avatar } from "../shell/Avatar";
import { PageHead } from "../shell/PageHead";
import { isFilmThumbnail, type MediaThumbnail } from "../components/attachments/kind";
import { greeting } from "./greeting";
import { useFirstReading } from "./useFirstReading";
import { WorkRow as ReplyRow } from "../studio/WorkRows";
import type { OverviewReply } from "./RecentReplies";
import "./overview-panels.css";

export interface OverviewItemData {
  id: string; title: string; state: "working" | "done" | "action"; status: string;
  context: Record<string, string>; action_label?: string;
  /** The picture the item shows.  `null`: the plugin named this item's picture and has none yet, so the tile stays
   *  plain; absent: it said nothing, and the workspace's own screenshot may stand in. */
  thumbnail?: (MediaThumbnail & { stale?: boolean }) | null;
  /** A quieter second line under the status: what goes on beside the work (a revision of a finished film). */
  note?: string;
}
export interface OverviewPreview { generation_id: string | null; items: OverviewItemData[]; workspace_thumbnail?: MediaThumbnail }
/** One line of the directory: who, in what state, and the one way in (`href`). */
export interface OverviewAgent {
  id: string; name: string; status: string; href: string; online?: boolean; loading?: boolean; notice?: string;
  /** Where an Owner goes to act on that notice, when there is somewhere. */
  noticeAction?: { label: string; href: string };
}
export interface OverviewCard extends OverviewItemData {
  agentId: string; agentName: string; panelKey: string; generation?: string | null; href: string; icon: IconName;
}

/** How many of each kind are shown before the rest are asked for. */
const LIMIT = { action: 4, working: 4, done: 3 } as const;

/** What the page says under its greeting: only what the Agents' own records say, in one sentence. */
function overviewLine(p: {
  arrived: boolean; pending: boolean; asks: number; working: number; agents: OverviewAgent[];
  incomplete: boolean; noneArrived: boolean;
}): string {
  if (!p.arrived) return "正在读取工作记录…";
  const who = p.agents.length === 1 ? p.agents[0].name : "";
  const parts: string[] = [];
  if (p.working) parts.push(who ? `${who}有 ${p.working} 项工作尚未完成` : `共有 ${p.working} 项工作尚未完成`);
  if (p.asks) parts.push(`有 ${p.asks} 件事在等你回答`);
  const said = parts.join("，");
  if (said) return `${said}。${p.incomplete ? "有的记录暂未读完整。" : ""}`;
  if (p.pending) return "正在读取工作记录…";
  if (p.incomplete) return "工作记录暂未读完整，可进入 IP 查看。";
  if (p.noneArrived) return "还没有 IP 连上来。完成连接后，它的工作会出现在这里。";
  if (!p.agents.length) return "从一个 IP 开始，工作与结果会汇集在这里。";
  return who ? `${who}现在没有在做的事。` : "你的 IP 现在都没有在做的事。";
}

/** Plugin summaries and workspace declarations are navigation, never nested applications. */
export function OverviewPanels({ cards, replies = [], pending, unavailable, unconnected = [], agents = [], accountName = "", now }: {
  cards: OverviewCard[]; pending: boolean;
  replies?: OverviewReply[];
  /** Agents whose records this page tried to read and could not. */
  unavailable: string[];
  /** Agents that have never reached this Hub: nothing to read, nothing failed. */
  unconnected?: string[];
  agents?: OverviewAgent[];
  /** Who is signed in, for the greeting. */
  accountName?: string;
  /** The moment the greeting is for; the page's own clock when absent. */
  now?: Date;
}) {
  const [expanded, setExpanded] = useState<string[]>([]);
  const arrived = useFirstReading(pending);
  const asks = cards.filter(card => card.state === "action");
  const working = cards.filter(card => card.state === "working");
  const done = cards.filter(card => card.state === "done");
  // Nothing here because nothing has arrived yet, which is not the same as
  // nothing here because a read failed.  A page that says the wrong one
  // sends an Owner looking for a fault that does not exist.
  const noneArrived = agents.length > 0 && agents.every(agent => unconnected.includes(agent.id));
  const line = overviewLine({ arrived, pending, asks: asks.length, working: working.length, agents,
    incomplete: unavailable.length > 0, noneArrived });
  const group = (key: keyof typeof LIMIT, title: string, items: OverviewCard[]) => {
    if (!items.length) return null;
    const all = expanded.includes(key);
    const shown = all ? items : items.slice(0, LIMIT[key]);
    return <section className={`owner-section overview-${key}`} aria-label={title}>
      <header className="owner-section-head"><h2>{title}</h2>{key === "action" ? null : <span className="owner-count num">{items.length}</span>}</header>
      <div className="overview-items">{shown.map(item => key === "action"
        ? <AskRow key={cardKey(item)} item={item} />
        : <WorkRow key={cardKey(item)} item={item} />)}</div>
      {items.length > LIMIT[key] ? <button className="btn btn-quiet overview-show-more" type="button"
        onClick={() => setExpanded(old => all ? old.filter(name => name !== key) : [...old, key])}>
        {all ? "收起" : `查看其余 ${items.length - LIMIT[key]} 项`}</button> : null}
    </section>;
  };
  return <div className="owner-page overview-page">
    <PageHead title={`${greeting(now ?? new Date())}${accountName ? `，${accountName}` : ""}`} meta={line}
      actions={<a className="btn" href="/#/create"><Icon name="plus" size={16} />新建 IP</a>} />
    {arrived ? <>
      {group("action", "等你回答", asks)}
      {group("working", "尚未完成", working)}
      {group("done", "最近完成", done)}
      {replies.length ? <section className="owner-section" aria-label="最近回复">
        <header className="owner-section-head"><h2>最近回复</h2><span className="owner-count num">{replies.length}</span></header>
        {(expanded.includes("replies") ? replies : replies.slice(0, 3)).map(reply => <ReplyRow key={`${reply.agentId}:${reply.id}`}
          title={reply.title} label="已回复" action="打开原对话" href={reply.href} agent={reply.agentName} when={reply.at} icon="chat" />)}
        {replies.length > 3 ? <button type="button" className="btn btn-quiet overview-show-more"
          onClick={() => setExpanded(old => old.includes("replies") ? old.filter(key => key !== "replies") : [...old, "replies"])}>
          {expanded.includes("replies") ? "收起" : `查看其余 ${replies.length - 3} 项`}</button> : null}
      </section> : null}
      <section className="owner-section overview-ips" aria-label="我的 IP">
        <header className="owner-section-head"><h2>我的 IP</h2><span className="owner-count num">{agents.length}</span></header>
        <div className="overview-ip-grid">
          {agents.map(agent => <AgentEntry key={agent.id} agent={agent} cards={cards.filter(card => card.agentId === agent.id)} />)}
          <a className="overview-ip-new" href="/#/create"><Icon name="plus" size={20} />再建一个 IP</a>
        </div>
      </section>
    </> : null}
  </div>;
}

const cardKey = (item: OverviewCard) => `${item.agentId}:${item.panelKey}:${item.generation}:${item.id}`;

/** One thing waiting on the Owner: who asked, what, and the one button that goes to it. */
function AskRow({ item }: { item: OverviewCard }) {
  const label = item.action_label || "回答";
  return <div className="owner-card overview-ask">
    <Avatar name={item.agentName} identity={item.agentId} size="lg" />
    <div className="overview-ask-copy">
      <h3 data-quoted="true">{item.title}</h3>
      <p data-quoted="true"><span>{item.agentName}</span>{item.status ? <> · {item.status}</> : null}</p>
    </div>
    <a className="btn btn-accent" href={item.href} data-panel={item.panelKey} aria-label={`${item.title}，${label}`}>{label}</a>
  </div>;
}

/** One Agent, once: its name and state as the one way in, and what stops it being usable, with where to fix it. */
function AgentEntry({ agent, cards }: { agent: OverviewAgent; cards: OverviewCard[] }) {
  const count = (state: OverviewCard["state"]) => cards.filter(card => card.state === state).length;
  const work = [[count("working"), "项未完成"], [count("action"), "件事等你回答"]]
    .filter(([n]) => n).map(([n, word]) => `${n} ${word}`).join(" · ");
  const note = agent.loading ? "正在读取工作界面…" : agent.notice;
  return <article className="owner-card overview-ip" data-state={agent.online ? "online" : "offline"}>
    <a className="overview-ip-identity" href={agent.href}>
      <Avatar name={agent.name} identity={agent.id} size="lg" />
      <span className="overview-ip-text"><h3 data-quoted="true">{agent.name}</h3>
        <small><i className={agent.online ? "is-online" : ""} />{agent.status}{work ? ` · ${work}` : ""}</small></span>
    </a>
    {note ? <p className="overview-ip-note" role="status">{note}
      {!agent.loading && agent.noticeAction ? <a href={agent.noticeAction.href}>{agent.noticeAction.label}<Icon name="arrow" size={14} /></a> : null}</p> : null}
  </article>;
}

function thumbnailUrl(agent: string, url: string): string | null {
  const prefix = `/agents/${encodeURIComponent(agent)}`;
  const path = url.startsWith("/api/panels/") ? prefix + url : url;
  return path.startsWith(`${prefix}/api/panels/`) && !path.includes("\\") && !path.includes("..") ? path : null;
}

/** The shape of a tile when its picture has not said what it is. */
const TILE_RATIO = 16 / 10;

/**
 * The picture in a tile. One narrower than the tile (a vertical film) is drawn whole and centred, and the sides are
 * filled with a blurred, dimmed copy of the same picture; one as wide as the tile or wider fills it as before.
 * Nothing is guessed about what to crop. The picture is not drawn until it has said how wide it is, so a vertical
 * film is never seen cropped first.
 */
function Thumbnail({ url, moving, icon, onFailed }: { url: string | null; moving: boolean; icon: IconName; onFailed: (url: string) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [narrow, setNarrow] = useState<boolean | null>(null);
  const measure = (width: number, height: number) => {
    if (!(width > 0 && height > 0)) return;
    const tile = box.current;
    const ratio = tile && tile.clientWidth > 0 && tile.clientHeight > 0 ? tile.clientWidth / tile.clientHeight : TILE_RATIO;
    setNarrow(width / height < ratio);
  };
  // A picture the browser already holds has loaded before anything listens.
  useLayoutEffect(() => {
    setNarrow(null);
    const picture = box.current?.querySelector<HTMLImageElement>("img:not(.overview-fill)");
    if (picture?.complete) measure(picture.naturalWidth, picture.naturalHeight);
  }, [url]);
  const picture = (fill: boolean) => {
    const common = { className: fill ? "overview-fill" : undefined, "aria-hidden": fill ? true : undefined, onError: fill ? undefined : () => onFailed(url!) };
    return moving
      ? <video {...common} src={`${url!}#t=0.1`} muted playsInline preload="metadata"
          onLoadedMetadata={fill ? undefined : event => measure(event.currentTarget.videoWidth, event.currentTarget.videoHeight)} />
      : <img {...common} src={url!} alt="" loading="lazy"
          onLoad={fill ? undefined : event => measure(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)} />;
  };
  return <div ref={box} className={`overview-thumbnail${narrow ? " is-narrow" : ""}`} data-shape={url ? narrow === null ? "pending" : narrow ? "narrow" : "wide" : undefined} aria-hidden="true">
    {url ? <>{narrow ? picture(true) : null}{picture(false)}</>
      : <span className="overview-preview-missing" title="预览尚未生成"><Icon name={icon} size={24} /></span>}
  </div>;
}

/** A state word this short goes in the pill; a longer sentence goes under the title, and the pill says only the kind. */
const PILL_MAX = 12;

/** One piece of work: its cover, who is on it and where it stands, and the quiet way in. */
function WorkRow({ item }: { item: OverviewCard }) {
  const url = item.thumbnail && thumbnailUrl(item.agentId, item.thumbnail.url);
  const [failed, setFailed] = useState<string | null>(null);
  const hasPreview = Boolean(url && failed !== url);
  // A film with no cover shows its own first frame; every other kind of card shows its picture.
  const video = isFilmThumbnail(item.thumbnail);
  const short = item.status.length <= PILL_MAX;
  // `working` is the plugin's unfinished group; it includes paused and failed work.
  const unfinished = item.state === "working";
  return <a className={`owner-card overview-work${hasPreview ? " has-preview" : ""}${unfinished ? "" : " is-done"}`} href={item.href} data-panel={item.panelKey}>
    <Thumbnail url={hasPreview ? url ?? null : null} moving={video} icon={item.icon} onFailed={setFailed} />
    <div className="overview-work-copy">
      <div className="overview-work-kicker"><Avatar name={item.agentName} identity={item.agentId} size="sm" /><span className="overview-agent" data-quoted="true">{item.agentName}</span>
        <span className={`owner-pill${unfinished ? "" : " is-done"}`}><span data-quoted="true">{short ? item.status : unfinished ? "未完成" : "已完成"}</span></span></div>
      <h3 data-quoted="true">{item.title}</h3>
      {short ? null : <p data-quoted="true">{item.status}</p>}
      {item.note ? <p className="overview-note" data-quoted="true">{item.note}</p> : null}
      {item.thumbnail?.stale ? <small className="overview-preview-age">图是上次的样子</small> : null}
    </div>
    <span className="owner-go">{unfinished ? "去看看" : item.action_label || "去看看"}<Icon name="arrow" size={16} /></span>
  </a>;
}
